import React from 'react';
import PropTypes from 'prop-types';
import './Button.css';

/**
 * Button Component
 * Shared button component for consistent styling across web and future mobile platforms
 *
 * Variants:
 * - primary: Main call-to-action buttons (gradient background)
 * - secondary: Less prominent actions (outline style)
 * - danger: Destructive actions (red/error color)
 * - ghost: Minimal style, text-only
 * - auth: Special styling for OAuth buttons
 *
 * Sizes:
 * - sm: Small buttons (e.g., inline actions)
 * - md: Medium/default size
 * - lg: Large buttons (e.g., primary CTAs)
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon,
  iconPosition = 'left',
  onClick,
  type = 'button',
  className = '',
  ariaLabel,
  ...rest
}) => {
  const buttonClasses = [
    'shared-button',
    `button-${variant}`,
    `button-${size}`,
    fullWidth && 'button-full-width',
    loading && 'button-loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-busy={loading}
      {...rest}
    >
      {loading && <span className="button-spinner" aria-hidden="true"></span>}
      {!loading && icon && iconPosition === 'left' && (
        <span className="button-icon button-icon-left" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="button-content">{children}</span>
      {!loading && icon && iconPosition === 'right' && (
        <span className="button-icon button-icon-right" aria-hidden="true">
          {icon}
        </span>
      )}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost', 'auth']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default Button;
